import React from 'react';

/**
 * Class representing an error that is to be displayed to the user.
 */
export class UiError {
  /**
   * Creates a UiError.
   * @param {string} key 
   *  Key of the error. Used to edit error in active errors.
   * @param {string} message 
   *  Message to display.
   * @param {*} severity 
   *  Severity of the error. One of:
   *  - danger
   *  - warning
   *  - info
   *  - resolved
   *  Corresponds with bootstrap class of alert.
   *  Resolved will clear the error.
   */
  constructor(
    key,
    message,
    severity = 'danger'
  ) {
    this.key = key;
    // This may accidentally be an Error...
    if(message instanceof Error) {
      this.message = message.message;
    }
    else if(typeof message === 'object' && message.hasOwnProperty('message')) {
      message = message.message;
    }
    // Allow react elements as error messages to allow for more complex
    // displays.
    else if (React.isValidElement(message)) {
      this.message = message
    }
    else if(typeof message !== 'string') {
      try {
        this.message = message.toString();
      }
      catch(error) {
        this.message = 'Unknown error.'
      }
      console.error(['Received invalid message in UiError:construct', message]);
    }
    else {
      this.message = message;
    }
    this.severity = severity;

    // Check type.
    if(
      !
      [
        'primary',
        'secondary',
        'success',
        'danger',
        'warning',
        'info',
        'light',
        'dark',
        'resolved',
        'persist',
      ].includes(this.severity) 
    ) {
      this.severity = 'danger';
    }
  }
}

/**
 * Updates the passed errors array.
 * Updates it if the key already exists.
 * Removes it if the key already exists and the severity is 'resolved'
 *
 * @param {UiError} uiError
 *  UiError object to process.
 * @param {[UiError]} errors
 *  Array of active error to add error to.
 *
 * @returns {[UiError]} errors
 *  Updated array of active errors.
 */
export function updateErrorsArray(uiError, errors) {
  // Find it in the array.
  for (let i = 0; i < errors.length; i++) {
    if(errors[i].key === uiError.key) {
      // Found the key.
      if(uiError.severity === 'resolved') {
        // remove it.
        errors.splice(i, 1);
        return errors;
      }
      // Or update it.
      errors[i] = uiError;
      return errors;
    }
  }
  // We didn't find the key. Add the error unless it's resolved.
  if(uiError.severity !== 'resolved') {
    errors.push(uiError);
  }
  return errors;
}

/**
 * Clears all errors that are not of type 'danger'
 *
 * @param {[uiError]} errors
 *    Array of active errors to clear.
 *
 * @returns {[UiError]} errors
 *  Updated array of active errors.
 */
export function clearNonPersistErrors(errors) {
  // Find it in the array.
  for (let i = 0; i < errors.length; i++) {
    if(errors[i].severity !== 'persist') {
      // remove it.
      errors.splice(i, 1);
    }
  }
  return errors;
}
